import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSound } from '../hooks/useSound';

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const CallToAction = () => {
  const navigate = useNavigate();
  const { playHover, playClick } = useSound();

  return (
    <section className="relative py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ...springConfig }}
          className="relative p-8 md:p-12 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm overflow-hidden"
        >
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -top-1/2 -right-1/2 w-full h-full bg-amber-400/10 rounded-full filter blur-3xl"
          />

          <div className="relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 1, ...springConfig }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                  Transform Your Business Today
                </span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
                Join hundreds of successful businesses that have accelerated their growth with ProfitIQ.AI
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 1, ...springConfig }}
            >
              <motion.button
                className="group relative px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg overflow-hidden shadow-lg shadow-amber-400/20 hover:shadow-amber-400/30"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={springConfig}
                onMouseEnter={playHover}
                onClick={() => {
                  playClick();
                  navigate('/consultation');
                }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-amber-300 to-amber-500"
                  initial={{ x: "100%" }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                />
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Get Started
                  <ChevronRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1" />
                </span>
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 1 }}
              className="mt-8 flex justify-center items-center gap-2 text-gray-400"
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>AI-powered insights for your business</span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};